/**
 * Panel Body Component
 *
 * A custom panel body component inspired by @wordpress/components PanelBody
 * with support for Responsive dropdown and additional customization options.
 *
 * @package RedfoxThankYou
 * @since 1.0.0
 */

import './style.scss';

import { Button } from '@wordpress/components';
import { chevronDown, chevronUp } from '@wordpress/icons';
import classNames from 'classnames';
import React, { useState } from 'react';

import { Responsive } from '../responsive-button-group';
import type { PanelBodyProps } from './types';

export const PanelBody: React.FC< PanelBodyProps > = ( {
	title,
	initialOpen = false,
	className = '',
	children,
	showResponsive = false,
	responsiveProps = {},
	collapsible = true,
	isParentGroup = false,
	icon,
	headerActions,
	...rest
} ) => {
	const [ isOpen, setIsOpen ] = useState( initialOpen );

	const togglePanel = () => {
		if ( collapsible ) {
			setIsOpen( ! isOpen );
		}
	};

	const panelClasses = classNames(
		'redfox-thank-you-panel-body',
		{
			'redfox-thank-you-panel-body--is-open': isOpen,
			'redfox-thank-you-panel-body--collapsible': collapsible,
			'redfox-thank-you-panel-body--with-responsive': showResponsive,
		},
		className,
	);

	const headerClasses = classNames( 'redfox-thank-you-panel-body__header', {
		'redfox-thank-you-panel-body__header--clickable': collapsible,
	} );

	return (
		<div className={ panelClasses } { ...rest }>
			{ /* Panel Header */ }
			<div className={ headerClasses }>
				<div
					className="redfox-thank-you-panel-body__title-row"
					onClick={ collapsible ? togglePanel : undefined }
					role={ collapsible ? 'button' : undefined }
					tabIndex={ collapsible ? 0 : undefined }
					onKeyDown={ ( e ) => {
						if (
							collapsible &&
							( e.key === 'Enter' || e.key === ' ' )
						) {
							e.preventDefault();
							togglePanel();
						}
					} }
				>
					{ /* Title Section */ }
					<div className="redfox-thank-you-panel-body__title-content">
						{ icon && (
							<span className="redfox-thank-you-panel-body__icon">
								{ icon }
							</span>
						) }
						{ title && (
							<h2 className="redfox-thank-you-panel-body__title">
								{ title }
							</h2>
						) }
					</div>

					{ /* Header Actions */ }
					{ headerActions && (
						<div
							className={classNames({
								'redfox-thank-you-panel-body__actions': true,
								'redfox-thank-you-panel-body__actions--show': isOpen,
							})}
							onClick={ ( e ) => e.stopPropagation() }
						>
							{ headerActions }
						</div>
					) }

					{ /* Controls Section */ }
					<div className="redfox-thank-you-panel-body__controls">
						{ /* Responsive Button Group */ }
						{ showResponsive && (
							<div
								className={classNames({
									'redfox-thank-you-panel-body__responsive': true,
									'redfox-thank-you-panel-body__responsive--show': isOpen,
								})}
								onClick={ ( e ) => e.stopPropagation() }
							>
								<Responsive
									{ ...responsiveProps }
									className={ classNames(
										'redfox-thank-you-panel-body__responsive-buttons',
										responsiveProps.className,
									) }
								/>
							</div>
						) }

						{ /* Collapse Toggle */ }
						{ collapsible && (
							<Button
								className="redfox-thank-you-panel-body__toggle"
								icon={ isOpen ? chevronUp : chevronDown }
								label={
									isOpen ? 'Collapse panel' : 'Expand panel'
								}
								variant="tertiary"
								size="small"
								iconSize={ 20 }
							/>
						) }
					</div>
				</div>
			</div>

			{ /* Panel Content */ }
			{ isOpen && (
				<div className={classNames({
					'redfox-thank-you-panel-body__content': true,
					'redfox-thank-you-panel-body__content--parent-group': isParentGroup,
				})}>
					{ children }
				</div>
			) }
		</div>
	);
};
